import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import Drawer from "./Drawer";
import { RESTPost } from "../services/types";
import { UserContext } from "../contexts/UserContext";
import { trashPost } from "../services/posts";
import { useAuthenticatedAction } from "../hooks/useAuthenticatedAction";
import CopyLinkButton from "./CopyLinkButton";
import "./AuthorDrawer.css";

interface AuthorDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    post: RESTPost;
    onPostDeleted?: () => void;
    orthoCorrection?: string;
}

const AuthorDrawer: React.FC<AuthorDrawerProps> = ({
    isOpen,
    onClose,
    post,
    onPostDeleted,
    orthoCorrection = ''
}) => {
    const { currentUser } = useContext(UserContext);
    const { execute: deletePostAction, loading: deleteLoading } = useAuthenticatedAction();
    const navigate = useNavigate();
    
    // Only render for the post author and if not trashed
    const isAuthor = currentUser && currentUser.id === post.author;
    const isTrashed = post.status === 'trash';
    if (!isAuthor || isTrashed) {
        return null;
    }
    
    // Check if there's an ortho correction (for highlighting and edit permission)
    const hasOrthoCorrection = () => {
        return orthoCorrection.trim() !== '';
    };

    // Check if the current user can edit (ortho correction takes precedence over 15 minutes rule)
    const canEdit = () => {
        // First check if there's an ortho correction message
        if (hasOrthoCorrection()) {
            return true;
        }
        
        // Otherwise check the 15 minutes rule
        if (!post.date) return false;
        const publishDate = new Date(post.date);
        const now = new Date();
        const diffInMinutes = (now.getTime() - publishDate.getTime()) / (1000 * 60);
        return diffInMinutes <= 15;
    };

    const handleDeletePost = async () => {
        if (!currentUser || deleteLoading) return;
        
        // Confirm deletion
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer votre BD ? Cette action est irréversible.")) {
            return;
        }
        
        const response = await deletePostAction(trashPost, post.id);
        
        if (response) {
            if (onPostDeleted) {
                onPostDeleted();
            }
            onClose();
        }
    };

    const handleEditPost = () => {
        navigate(`/editor?edit=${post.id}`);
        onClose();
    };

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title="Gérer ma BD"
        >
            <div className="AuthorDrawer-content">
                <div className="AuthorDrawer-postInfo">
                    <div className="AuthorDrawer-postInfo-header">
                        <div>
                            <h3 dangerouslySetInnerHTML={{__html: post.title.rendered}} />
                            <p>Publiée le {post.date ? new Date(post.date).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }) : 'Date inconnue'}</p>
                        </div>
                        <CopyLinkButton link={post.link} />
                    </div>
                </div>
                
                <div className="AuthorDrawer-actions">
                    {canEdit() && (
                        <button 
                            className={`AuthorDrawer-action edit ${hasOrthoCorrection() ? 'ortho-correction' : ''}`}
                            onClick={handleEditPost}
                        >
                            <span className="action-text">Modifier ma BD</span>
                        <span className="action-hint">
                            {hasOrthoCorrection() 
                                ? `"${orthoCorrection}"` 
                                : `Possible pendant ${Math.max(0, Math.round(15 - ((new Date().getTime() - new Date(post.date || '').getTime()) / (1000 * 60))))} min`
                            }
                        </span>
                        </button>
                    )}
                    
                    <button 
                        className={`AuthorDrawer-action danger ${deleteLoading ? 'loading' : ''}`}
                        onClick={handleDeletePost}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Suppression...' : 'Supprimer ma BD'}
                    </button>
                </div>
            </div>
        </Drawer>
    );
};

export default AuthorDrawer;
